import { UNIT_LABELS, UNIT_TYPES, type Balance, type UnitType } from '@bienenkampf/shared';
import { queenKillChance, resolveCombat, totalOf, type UnitCounts } from '@bienenkampf/sim';
import { Badge, Input } from '@bienenkampf/ui';
import { useMemo, useState } from 'react';
import { formatNumber } from '../lib/time.js';

export type UnitDraft = Partial<Record<UnitType, string>>;

export function draftToCounts(d: UnitDraft): UnitCounts {
  const out: UnitCounts = {};
  for (const t of UNIT_TYPES) {
    const n = Math.max(0, Math.floor(Number(d[t]) || 0));
    if (n > 0) out[t] = n;
  }
  return out;
}

export function UnitInputs({
  value,
  onChange,
  max,
  compact,
}: {
  value: UnitDraft;
  onChange: (next: UnitDraft) => void;
  max?: Partial<Record<UnitType, number>>;
  compact?: boolean;
}) {
  return (
    <div className={compact ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-2 gap-2 sm:grid-cols-3'}>
      {UNIT_TYPES.map((t) => {
        const m = max?.[t];
        if (max && (m ?? 0) === 0) return null;
        return (
          <Input
            key={t}
            name={`u-${t}`}
            type="number"
            inputMode="numeric"
            min={0}
            max={m}
            label={m !== undefined ? `${UNIT_LABELS[t]} (${formatNumber(m)})` : UNIT_LABELS[t]}
            placeholder="0"
            value={value[t] ?? ''}
            onChange={(e) => onChange({ ...value, [t]: e.target.value })}
            className="text-right"
          />
        );
      })}
    </div>
  );
}

/**
 * Offizieller Kampfsimulator: dieselbe reine Funktion wie der Worker (Konzept, Abschnitt 3).
 * Verteidiger sind Annahmen des Spielers (oder aus dem letzten Spaehbericht vorbelegt).
 */
export function CombatSimulator({
  balance,
  attackers,
  defenders,
  onDefendersChange,
  guardCombLevel,
  onGuardCombChange,
  winter,
  onWinterChange,
  seasons,
}: {
  balance: Balance;
  attackers: UnitCounts;
  defenders: UnitDraft;
  onDefendersChange: (d: UnitDraft) => void;
  guardCombLevel: number;
  onGuardCombChange: (n: number) => void;
  winter: boolean;
  onWinterChange: (w: boolean) => void;
  seasons: Record<
    'TRACHT' | 'WINTER',
    { combatMult: string | number; defenderBonus: string | number }
  >;
}) {
  const [open, setOpen] = useState(true);
  const result = useMemo(() => {
    const def = draftToCounts(defenders);
    if (totalOf(attackers) === 0) return null;
    const s = seasons[winter ? 'WINTER' : 'TRACHT'];
    return resolveCombat(balance, attackers, def, {
      combatMult: Number(s.combatMult),
      guardCombLevel,
      defenderBonus: Number(s.defenderBonus),
      inHive: true,
    });
  }, [balance, attackers, defenders, guardCombLevel, winter, seasons]);

  return (
    <div className="rounded-lg border border-stone-800 p-3">
      <button
        type="button"
        className="flex w-full items-center justify-between text-left text-sm font-medium"
        onClick={() => setOpen((o) => !o)}
      >
        <span>⚔ Kampfsimulator</span>
        <span className="text-stone-500">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="mt-3 flex flex-col gap-3">
          <div>
            <div className="mb-1 text-xs text-stone-400">Angenommene Verteidiger im Stock</div>
            <UnitInputs value={defenders} onChange={onDefendersChange} compact />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-28">
              <Input
                name="guard-comb"
                type="number"
                inputMode="numeric"
                label="Wächterwabe"
                min={0}
                max={30}
                value={String(guardCombLevel)}
                onChange={(e) =>
                  onGuardCombChange(Math.max(0, Math.floor(Number(e.target.value) || 0)))
                }
              />
            </div>
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={winter}
                onChange={(e) => onWinterChange(e.target.checked)}
              />
              Winter (Wintertraube, halbe Kampfkraft)
            </label>
          </div>
          {result ? (
            <div className="rounded-lg bg-stone-900 p-3 text-sm">
              <div className="flex items-center justify-between">
                <Badge tone={result.attackerWins ? 'green' : 'red'}>
                  {result.attackerWins ? 'Angreifer gewinnt' : 'Verteidiger hält'}
                </Badge>
                <span className="tabular-nums text-stone-400">
                  A {formatNumber(result.attacker.power)} : D {formatNumber(result.defender.power)}{' '}
                  · r = {Number.isFinite(result.ratio) ? result.ratio.toFixed(2) : '∞'}
                </span>
              </div>
              <div className="mt-2 flex flex-col gap-3 text-xs">
                <LossTable title="Angreifer" side={result.attacker} />
                <LossTable title="Verteidiger" side={result.defender} />
                {result.attackerWins && (
                  <p className="text-stone-400">
                    👑 Königin fällt mit {Math.round(queenKillChance(balance, result.ratio) * 100)}{' '}
                    %
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-stone-500">Trag oben Bienen für den Angriff ein.</p>
          )}
        </div>
      )}
    </div>
  );
}

function LossTable({
  title,
  side,
}: {
  title: string;
  side: { before: UnitCounts; losses: UnitCounts; after: UnitCounts; lossRate: number };
}) {
  const rows = UNIT_TYPES.filter((t) => (side.before[t] ?? 0) > 0);
  return (
    <div>
      <div className="mb-1 font-medium">
        {title}{' '}
        <span className="font-normal text-stone-400">−{Math.round(side.lossRate * 100)} %</span>
      </div>
      {rows.length === 0 && <div className="text-stone-500">–</div>}
      {rows.map((t) => (
        <div key={t} className="flex justify-between gap-2 tabular-nums">
          <span>{UNIT_LABELS[t]}</span>
          <span className="whitespace-nowrap">
            {formatNumber(side.before[t] ?? 0)} →{' '}
            <span className="text-red-400">−{formatNumber(side.losses[t] ?? 0)}</span> ={' '}
            {formatNumber(side.after[t] ?? 0)}
          </span>
        </div>
      ))}
    </div>
  );
}
